import { useWizard } from "../CalendarForm";
import { CardSelector } from "../components/inputs";

/**
 * Step1GoalSelection Component
 * 
 * First step where users select their training goal.
 */

const GOAL_OPTIONS = [
  {
    id: "bulk",
    label: "Bulk",
    subtitle: "Build Muscle Mass",
    description: "Focus on muscle growth and strength gains with caloric surplus.",
    icon: "💪",
    color: "blue",
  },
  {
    id: "cut",
    label: "Cut",
    subtitle: "Lose Body Fat",
    description: "Optimize fat loss while preserving muscle with caloric deficit.",
    icon: "🔥",
    color: "rose",
  },
  {
    id: "metabolic",
    label: "Metabolic Building",
    subtitle: "Body Recomposition",
    description: "Balance muscle gain and fat loss for optimal metabolic health.",
    icon: "⚡",
    color: "amber",
  },
];

// Color mappings for goal cards (more elaborate than standard CardSelector)
const COLOR_CLASSES = {
  blue: {
    selected: "bg-blue-50 border-blue-500 ring-2 ring-blue-200",
    icon: "bg-blue-100",
    iconSelected: "bg-blue-500",
  },
  rose: {
    selected: "bg-rose-50 border-rose-500 ring-2 ring-rose-200",
    icon: "bg-rose-100",
    iconSelected: "bg-rose-500",
  },
  amber: {
    selected: "bg-amber-50 border-amber-500 ring-2 ring-amber-200",
    icon: "bg-amber-100",
    iconSelected: "bg-amber-500",
  },
};

export default function Step1GoalSelection() {
  const { formData, updateFormData } = useWizard();

  const handleSelect = (goalId) => {
    updateFormData({ goal: goalId });
  };

  return (
    <div className="space-y-6">
      {/* Instruction Text */}
      <div className="text-center mb-6">
        <p className="text-gray-700 text-base">
          What is the primary training objective?
        </p>
        <p className="text-gray-400 text-sm mt-1">
          This will customize the configuration options
        </p>
      </div>

      {/* Goal Options Grid - Custom implementation for larger cards */}
      <div className="grid gap-4 md:grid-cols-3">
        {GOAL_OPTIONS.map((option) => {
          const isSelected = formData.goal === option.id;
          const colors = COLOR_CLASSES[option.color];

          return (
            <button
              key={option.id}
              type="button"
              onClick={() => handleSelect(option.id)}
              className={`
                relative p-5 rounded-xl text-left transition-all duration-200
                border-2 group
                ${isSelected
                  ? colors.selected
                  : "bg-white border-gray-200 hover:border-gray-300 hover:shadow-sm"
                }
              `}
            >
              {/* Selection Indicator */}
              {isSelected && (
                <div className="absolute top-3 right-3">
                  <div className="w-5 h-5 bg-indigo-600 rounded-full flex items-center justify-center">
                    <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                </div>
              )}

              {/* Icon */}
              <div
                className={`
                  w-12 h-12 rounded-lg flex items-center justify-center mb-3
                  transition-colors duration-200 text-2xl
                  ${isSelected ? colors.iconSelected : colors.icon}
                `}
              >
                {option.icon}
              </div>

              {/* Title */}
              <h3 className="text-lg font-semibold text-gray-900 mb-1">
                {option.label}
              </h3>

              {/* Subtitle */}
              <p className="text-sm font-medium text-gray-500 mb-2">
                {option.subtitle}
              </p>

              {/* Description */}
              <p className="text-sm text-gray-400 leading-relaxed">
                {option.description}
              </p>
            </button>
          );
        })}
      </div>

      {/* Helper Text */}
      <p className="text-center text-gray-400 text-sm mt-4">
        {formData.goal 
          ? `Selected: ${GOAL_OPTIONS.find(o => o.id === formData.goal)?.label}. Click Next to continue.`
          : "Select an option above to continue"
        }
      </p>
    </div>
  );
}